import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { RENT } from 'Defaults'
import { PLURAL_AREA_TYPE } from 'Defaults/map'
import WithSpinner from 'Components/WithSpinner'
import { isNilEmpty } from 'Utils'
import ShowOnVisible from 'Components/ShowOnVisible'
import { Collapsible } from 'Components'
// import WithLoginBlurBox from 'Components/WithLoginBlurBox'
import SimpleChartsV2, {
  BuildingChartsV2,
  MaintenanceChartsV2,
} from 'Components/SimpleChartsV2'

import {
  useFetchBuildingStats,
  useFetchBuildingMaintStats,
  useFetchListingStats,
} from 'Components/SimpleChartsV2/statApis'

// import css from './styles.js'
import { _Disclaimer, collapseContainerCss } from './styles'


const Index = ({
  listing,
  googleAnalyticsEvent,
  collapsibleIdSuffix,
}) => {
  const [startScroll, updateStartScroll] = useState(false)
  const [contentVisibility, updateContentVisibility] = useState(false)
  const buildingId = listing && listing.building && listing.building.id
  const lowestArea = (listing && listing.lowest_area) || {}
  // fetch data on visible
  const {
    data: statsData,
    isFetching: isFetchingStats,
  } = useFetchBuildingStats({ buildingId, enabled: Boolean(buildingId && contentVisibility) })

  const {
    data: maintenanceData,
  } = useFetchBuildingMaintStats({ buildingId, enabled: Boolean(buildingId && contentVisibility) })

  const {
    data: listingAreaStats,
  } = useFetchListingStats({
    areaType: PLURAL_AREA_TYPE[lowestArea.area_type],
    areaId: lowestArea.id,
    enabled: Boolean(lowestArea?.id && contentVisibility),
  })

  const { area_stats: buildingAreaStats, building_stats: buildingStats } = statsData || listingAreaStats || {}

  useEffect(() => {
    const handleScroll = () => {
      // Preload the charts to fix the laggy scrolling issue caused by loading recharts
      if(!startScroll && SimpleChartsV2.preload) { SimpleChartsV2.preload() }
      updateStartScroll(true)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const triggerOnStatBlockVisible = () => {
    updateContentVisibility(true)
  }

  const ifStats = Boolean(
    listingAreaStats || buildingAreaStats || buildingStats,
  )
  const isRent = listing && listing.offer === RENT
  const ifMaint = Boolean(
    maintenanceData &&
    !isNilEmpty(maintenanceData.maintenance_fees) &&
    !isRent,
  )
  let locationText = ''
  let buildingName = ''
  let addressName = ''
  if(listing) {
    const { building } = listing
    buildingName = building ? building.name : ''
    locationText = (lowestArea && lowestArea.display_name) || buildingName
    addressName = listing.title
  }
  // compare price and maintenance fee
  const collapsibleSeoText = `Compare stats for ${buildingName || addressName} with ${locationText}`
  if(listing) {
    return (
      <Collapsible
        inputId={`InvestorInsights-${collapsibleIdSuffix}`}
        title="Values & Trends"
        seoText={collapsibleSeoText}
        customClasses={{
          collapseContainer: collapseContainerCss,
        }}
      >
        <div key={`listing-charts-${listing && listing.id}`}>
          <WithSpinner showSpinner={!!isFetchingStats} />
          <ShowOnVisible
            onVisibleHandler={triggerOnStatBlockVisible}
            offset={{ bottom: -200 }}
          >
            {ifStats && (
              listing.building ? (
                <BuildingChartsV2
                  buildingId={listing.building_id}
                  buildingName={listing.building && listing.building.name}
                  areaUrl={listing.lowest_area && listing.lowest_area.url}
                  areaStats={buildingAreaStats}
                  isFetchingStats={isFetchingStats}
                  buildingStats={buildingStats}
                  areaName={
                    listing.lowest_area && listing.lowest_area.display_name
                  }
                />
              ) : (
                <SimpleChartsV2
                  address={listing.title}
                  areaStats={buildingAreaStats}
                  buildingStats={buildingStats}
                  areaUrl={
                    listing &&
                    listing.lowest_area &&
                    listing.lowest_area.url
                  }
                  areaName={
                    listing.lowest_area && listing.lowest_area.display_name
                  }
                  source={'listing'}
                  googleAnalyticsEvent={googleAnalyticsEvent}
                />
              )
            )}

            {listing.building && ifMaint && (
              <MaintenanceChartsV2
                buildingId={listing && listing.building_id}
                buildingName={
                  listing && listing.building && listing.building.name
                }
                maintenanceData={maintenanceData}
              />
            )}
            {(ifMaint || ifStats) && (
              <_Disclaimer>
                * Based on analysis of information from past MLS® listings
              </_Disclaimer>
            )}
          </ShowOnVisible>
        </div>
      </Collapsible>
    )
  }
  return null
}


Index.propTypes = {
  listing: PropTypes.shape({
    id: PropTypes.number,
    building_id: PropTypes.number,
    title: PropTypes.string,
    building: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    lowest_area: PropTypes.shape({
      display_name: PropTypes.string,
      id: PropTypes.number,
      url: PropTypes.string,
      area_type: PropTypes.string,
    }),
    offer: PropTypes.string,
  }),
  googleAnalyticsEvent: PropTypes.func,
  listingAreaStats: PropTypes.shape({}),
  buildingAreaStats: PropTypes.shape({}),
  buildingStats: PropTypes.shape({}),
  maintenanceData: PropTypes.shape({
    maintenance_fees: PropTypes.arrayOf(PropTypes.shape({})),
    parking_fees: PropTypes.number,
    locker_fees: PropTypes.number,
  }),
  collapsibleIdSuffix: PropTypes.string,
}

export default Index
